import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FreelancerDataService } from '../../services/freelancer-data.service';
import { AuthService } from './service/auth.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';

type CommunityLookup = { data?: { space_id?: number; Spaces?: { id?: number } } };
const BY_SLUG_ENDPOINT = (slug: string) =>
  `${environment.apiUrl}/communities/${encodeURIComponent(slug)}`;

@Injectable({ providedIn: 'root' })
export class CommunityMembershipGuard implements CanActivate {
  constructor(
    private http: HttpClient,
    private dataService: FreelancerDataService,
    private auth: AuthService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const paramCommunityId = Number(route.paramMap.get('communityId') || 0);
    const paramSlug = (route.paramMap.get('slug') || '').trim();

    if (!this.auth.isLoggedIn()) {
      return of(this.router.parseUrl(`/${paramSlug}/${paramCommunityId}/login`));
    }
    if (!paramCommunityId || !paramSlug) return of(true);

    // 1) Find target space (prefer pending; else fetch by slug)
    const pendingSid = Number(sessionStorage.getItem('pendingSpaceID') || 0) || 0;

    const resolveTargetSpaceId$ = pendingSid
      ? of(pendingSid)
      : this.http.get<CommunityLookup>(BY_SLUG_ENDPOINT(paramSlug)).pipe(
          map((res) => Number(res?.data?.space_id || res?.data?.Spaces?.id || 0) || 0)
        );

    return resolveTargetSpaceId$.pipe(
      switchMap((targetSpaceId) => {
        if (!targetSpaceId) return of(true);

        const currentSpaceId =
          Number(localStorage.getItem('spaceID') || 0) || this.auth.getSpaceIdFromToken() || 0;

        if (currentSpaceId === targetSpaceId) {
          // already correct space
          localStorage.setItem('spaceID', String(targetSpaceId));
          this.auth.emitAuthChange();
          return of(true);
        }

        const userId = this.auth.getUserId();
        if (!userId) return of(true);

        // Make sure swap call goes with target space header (your HeaderInterceptor will prefer pending)
        sessionStorage.setItem('pendingSpaceID', String(targetSpaceId));

        return this.dataService.switchCommunityToken({ user_id: +userId, space_id: +targetSpaceId }).pipe(
          map((resp: any) => {
            sessionStorage.removeItem('pendingSpaceID');

            const token = resp?.data?.token;
            const sid = Number(resp?.data?.space_id || targetSpaceId);
            if (token && sid) {
              this.auth.setToken(token);
              localStorage.setItem('spaceID', String(sid));
              localStorage.setItem('slug', paramSlug);
              localStorage.setItem('communityId', String(paramCommunityId));
              this.auth.emitAuthChange();
              return true;
            }
            this.toastr.error('Failed to switch community token.');
            return true;
          }),
          catchError(() => {
            sessionStorage.removeItem('pendingSpaceID');
            this.toastr.error('Failed to switch community token.');
            return of(true);
          })
        );
      }),
      // 2) membership check after swap/current space is correct
      switchMap(() => {
        const activeSpaceId = Number(localStorage.getItem('spaceID') || '0');
        return this.dataService.getUserCommunities({}, activeSpaceId).pipe(
          map((res: any) => {
            const communities: any[] = res?.data || [];
            const isMember = communities.some(
              (c) => Number(c.id) === paramCommunityId && c.community_url === paramSlug
            );
            if (isMember) return true;
            this.toastr.error('You are not a member of this community.');
            return this.router.parseUrl(`/${paramSlug}/${paramCommunityId}/login`);
          }),
          catchError(() => of(this.router.parseUrl(`/${paramSlug}/${paramCommunityId}/login`)))
        );
      })
    );
  }
}
